// src/components/admin/pages/AdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Key, KeySquare } from 'lucide-react';
import  Card  from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import  Input  from '@/components/ui/Input';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { useAdminAuth } from '../../../context/AdminAuthContext';

const AdminLogin = () => {
  const [step, setStep] = useState('credentials'); // credentials, mfa, changePassword
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [mfaToken, setMfaToken] = useState('');
  const [newPassword, setNewPassword] = useState({ password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempAdminData, setTempAdminData] = useState(null);
  const navigate = useNavigate();
  const { loginAdmin } = useAdminAuth();

  const API_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:5000/api';

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
  
    try {
      const response = await fetch(`${API_URL}/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
  
      const data = await response.json();
      console.log('Login response:', data);  // Add this log
  
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
  
      if (data.token) {
        console.log('Login successful, token:', data.token); // Add this log
        if (data.admin.requirePasswordChange) {
          setTempAdminData(data);
          setStep('changePassword');
        } else {
          console.log('Attempting to login with token');  // Add this log
          await loginAdmin(data.token);
          console.log('Login successful, navigating...');  // Add this log
          navigate('/admin');
        }
      }
    } catch (err) {
      console.error('Login error:', err);  // Improve error logging
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMFASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/mfa/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: mfaToken,
          adminId: tempAdminData.admin.id
        })
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        throw new Error('Invalid MFA token');
      }

      if (tempAdminData.requirePasswordChange) {
        setStep('changePassword');
      } else {
        await loginAdmin(tempAdminData.token);
        navigate('/admin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.password !== newPassword.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/password/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempAdminData.token}`
        },
        body: JSON.stringify({
          newPassword: newPassword.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      await loginAdmin(tempAdminData.token);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
          <p className="text-gray-600 mt-2">Sign in to manage your LocalFlow system</p>
        </div>

        {step === 'credentials' && (
          <form onSubmit={handleCredentialsSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="email"
                  required
                  className="pl-10"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  placeholder="admin@localflow.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="password"
                  required
                  className="pl-10"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        )}

        {step === 'mfa' && (
          <form onSubmit={handleMFASubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MFA Token
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  required
                  className="pl-10"
                  value={mfaToken}
                  onChange={(e) => setMfaToken(e.target.value)}
                  placeholder="Enter your 6-digit code"
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex space-x-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setStep('credentials')}
              >
                Back
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </form>
        )}

        {step === 'changePassword' && (
          <form onSubmit={handlePasswordChange} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeySquare className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="password"
                  required
                  className="pl-10"
                  value={newPassword.password}
                  onChange={(e) => setNewPassword({ ...newPassword, password: e.target.value })}
                  placeholder="Enter new password"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeySquare className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="password"
                  required
                  className="pl-10"
                  value={newPassword.confirmPassword}
                  onChange={(e) => setNewPassword({ ...newPassword, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
};

export default AdminLogin;